import { useState, useEffect } from "react"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  File,
  Folder,
  Search,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from "lucide-react"
import filesApi, { type FileItem } from "@crm-src/lib/files-api"
import { FileTypeIcon } from "./file-manager/file-type-icon"

export interface SelectedFile {
  id: string
  name: string
  size: number
  type: string
  downloadUrl: string
}

interface FileManagerSelectorDialogProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (files: SelectedFile[]) => void
  maxFiles?: number
  selectedFileIds?: string[]
  title?: string
  description?: string
}

export default function FileManagerSelectorDialog({
  isOpen,
  onClose,
  onSelect,
  maxFiles = 5,
  selectedFileIds = [],
  title = "Select Files from File Manager",
  description = "Choose files from your file manager to attach to the email campaign."
}: FileManagerSelectorDialogProps) {
  const { toast } = useToast()

  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined)
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{id?: string, name: string}>>([
    { name: "Root", id: undefined }
  ])

  // Files state
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Selection state
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set(selectedFileIds))

  // Search state
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)

  // Load files when dialog opens or folder changes
  useEffect(() => {
    if (isOpen) {
      loadFiles()
    }
  }, [isOpen, currentFolderId])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentFolderId(undefined)
      setBreadcrumbs([{ name: "Root", id: undefined }])
      setSearchQuery("")
      setSelectedFiles(new Set(selectedFileIds))
      setError(null)
    }
  }, [isOpen, selectedFileIds])

  const loadFiles = async () => {
    try {
      setLoading(true)
      setError(null)

      if (searchQuery.trim()) {
        setIsSearching(true)
        const searchResults = await filesApi.search(searchQuery.trim(), currentFolderId)
        setFiles(searchResults)
        setIsSearching(false)
      } else {
        const folderContents = await filesApi.getFolderContents(currentFolderId)
        setFiles(folderContents)
      }
    } catch (error) {
      console.error('Error loading files:', error)
      setError('Failed to load files')
      toast({
        title: "Error",
        description: "Failed to load files from file manager",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim()) {
      await loadFiles()
    } else {
      await loadFiles()
    }
  }

  const navigateToFolder = (folder: FileItem) => {
    setCurrentFolderId(folder.id)
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }])
    setSearchQuery("") // Clear search when navigating
  }

  const navigateToBreadcrumb = (index: number) => {
    const targetBreadcrumb = breadcrumbs[index]
    setCurrentFolderId(targetBreadcrumb.id)
    setBreadcrumbs(breadcrumbs.slice(0, index + 1))
    setSearchQuery("") // Clear search when navigating
  }

  const toggleFileSelection = (file: FileItem) => {
    // Don't allow folder selection
    if (file.is_folder) {
      toast({
        title: "Cannot attach folders",
        description: "Only files can be attached to email campaigns",
        variant: "destructive",
      })
      return
    }

    const newSelection = new Set(selectedFiles)

    if (newSelection.has(file.id)) {
      newSelection.delete(file.id)
    } else {
      if (newSelection.size >= maxFiles) {
        toast({
          title: "Maximum files reached",
          description: `You can only select up to ${maxFiles} files`,
          variant: "destructive",
        })
        return
      }
      newSelection.add(file.id)
    }

    setSelectedFiles(newSelection)
  }

  const handleSelect = async () => {
    const selectedFilesList = files.filter(file =>
      selectedFiles.has(file.id) && !file.is_folder
    )

    const selectedFileData: SelectedFile[] = await Promise.all(
      selectedFilesList.map(async (file) => ({
        id: file.id,
        name: file.name,
        size: file.file_size || 0,
        type: file.content_type || 'application/octet-stream',
        downloadUrl: await filesApi.getDownloadUrl(file.id)
      }))
    )

    onSelect(selectedFileData)
    onClose()
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const selectedCount = selectedFiles.size
  const displayFiles = files.filter(file => !file.is_folder) // Only show files for selection
  const displayFolders = files.filter(file => file.is_folder && searchQuery.trim() === '') // Only show folders for navigation when not searching

  const folderRowSx = {
    display: "flex",
    alignItems: "center",
    p: 1.5,
    borderRadius: 1,
    cursor: "pointer",
    "&:hover": { bgcolor: "action.hover" },
  } as const

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { display: "flex", flexDirection: "column" } }}>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        {title}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{description}</Typography>
      </DialogTitle>

      <DialogContent sx={{ overflowY: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {/* Search Bar */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <Box sx={{ display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}>
                    <Loader2 size={16} />
                  </Box>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Breadcrumbs */}
          {!searchQuery && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", color: "text.secondary" }}>
              {breadcrumbs.map((crumb, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {index > 0 && <Box component="span">/</Box>}
                  <Box
                    component="button"
                    onClick={() => navigateToBreadcrumb(index)}
                    disabled={index === breadcrumbs.length - 1}
                    sx={{
                      background: "none",
                      border: "none",
                      p: 0,
                      font: "inherit",
                      color: "inherit",
                      cursor: index === breadcrumbs.length - 1 ? "default" : "pointer",
                      "&:not(:disabled):hover": { color: "primary.main", textDecoration: "underline" },
                    }}
                  >
                    {crumb.name}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Files List */}
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Box sx={{ maxHeight: "40vh", overflowY: "auto" }}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                  <Box sx={{ display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}>
                    <Loader2 size={24} />
                  </Box>
                  <Typography component="span" sx={{ ml: 1 }}>Loading files...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4, color: "error.main" }}>
                  <AlertCircle size={20} />
                  <Typography component="span" sx={{ ml: 1 }}>{error}</Typography>
                </Box>
              ) : displayFiles.length === 0 && displayFolders.length === 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4, color: "text.disabled" }}>
                  <File size={24} />
                  <Typography component="span" sx={{ ml: 1 }}>
                    {searchQuery ? 'No files found matching your search' : 'This folder is empty'}
                  </Typography>
                </Box>
              ) : displayFiles.length === 0 && displayFolders.length > 0 ? (
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Back button when in a subfolder */}
                  {!searchQuery && currentFolderId && (
                    <Box
                      sx={folderRowSx}
                      onClick={() => {
                        const parentIndex = breadcrumbs.length - 2
                        if (parentIndex >= 0) {
                          navigateToBreadcrumb(parentIndex)
                        }
                      }}
                    >
                      <Box sx={{ mr: 1.5, color: "text.disabled", display: "flex" }}><ArrowLeft size={16} /></Box>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Back to parent folder</Typography>
                    </Box>
                  )}

                  {/* Folders */}
                  {displayFolders.map((folder) => (
                    <Box key={folder.id} sx={folderRowSx} onClick={() => navigateToFolder(folder)}>
                      <Box sx={{ color: "info.main", mr: 1.5, display: "flex" }}><Folder size={16} /></Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{folder.name}</Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>Folder</Typography>
                      </Box>
                    </Box>
                  ))}

                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3, color: "text.disabled" }}>
                    <File size={20} />
                    <Typography component="span" variant="body2" sx={{ ml: 1 }}>No files in this folder to attach</Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Back button when in a subfolder */}
                  {!searchQuery && currentFolderId && (
                    <Box
                      sx={folderRowSx}
                      onClick={() => {
                        const parentIndex = breadcrumbs.length - 2
                        if (parentIndex >= 0) {
                          navigateToBreadcrumb(parentIndex)
                        }
                      }}
                    >
                      <Box sx={{ mr: 1.5, color: "text.disabled", display: "flex" }}><ArrowLeft size={16} /></Box>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Back to parent folder</Typography>
                    </Box>
                  )}

                  {/* Folders */}
                  {displayFolders.map((folder) => (
                    <Box key={folder.id} sx={folderRowSx} onClick={() => navigateToFolder(folder)}>
                      <Box sx={{ color: "info.main", mr: 1.5, display: "flex" }}><Folder size={16} /></Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{folder.name}</Typography>
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>Folder</Typography>
                      </Box>
                    </Box>
                  ))}

                  {/* Separator between folders and files */}
                  {displayFolders.length > 0 && displayFiles.length > 0 && (
                    <Box sx={{ borderTop: 1, borderColor: "divider", my: 1.5 }} />
                  )}

                  {/* Files section header */}
                  {displayFiles.length > 0 && (
                    <Box sx={{ px: 1.5, py: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Select Files to Attach
                      </Typography>
                    </Box>
                  )}

                  {/* Files */}
                  {displayFiles.map((file) => {
                    const isSelected = selectedFiles.has(file.id)
                    return (
                      <Box
                        key={file.id}
                        onClick={() => toggleFileSelection(file)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          borderRadius: 1,
                          border: 1,
                          transition: "background-color .15s, border-color .15s",
                          cursor: "pointer",
                          ...(isSelected
                            ? {
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                                borderColor: "primary.main",
                                "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.1) },
                              }
                            : {
                                borderColor: "transparent",
                                "&:hover": { bgcolor: "action.hover", borderColor: "divider" },
                              }),
                        }}
                      >
                        <Checkbox
                          size="small"
                          sx={{ mr: 1.5, p: 0.5 }}
                          checked={isSelected}
                          onChange={() => toggleFileSelection(file)}
                        />
                        <Box sx={{ display: "flex", flexShrink: 0 }}>
                          <FileTypeIcon file={file} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, ml: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.75rem", color: "text.disabled" }}>
                            <Box component="span">{formatFileSize(file.file_size || 0)}</Box>
                            <Box component="span">•</Box>
                            <Box component="span">{new Date(file.created_at).toLocaleDateString()}</Box>
                          </Box>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>
          </Box>

          {/* Selection Info */}
          {selectedCount > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, bgcolor: (t) => alpha(t.palette.primary.main, 0.05), borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500 }}>
                {selectedCount} file{selectedCount === 1 ? '' : 's'} selected
              </Typography>
              <Chip label={`${selectedCount}/${maxFiles}`} size="small" />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: "divider", flexShrink: 0, bgcolor: "background.paper" }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          disabled={selectedCount === 0}
        >
          Select {selectedCount > 0 ? `${selectedCount} ` : ''}Files
        </Button>
      </DialogActions>
    </Dialog>
  )
}
